import { Component, OnChanges, Input, EventEmitter, Output } from "@angular/core";
import { SeriesLineOptions } from "highcharts";

import { APIResults } from "src/app/sharedinterfaces";
import { ApiService } from "src/app/api.service";
import { ChartingService } from "src/app/charting.service";
import { AdjustmentArchive } from "../adjustment-history/adjustment-history.component";
import { DvsUtilities } from "src/app/dvs-utilities";
declare const BrandVars;

@Component({
	selector: "app-rtb-balance",
	templateUrl: "./rtb-balance.component.html",
	styleUrls: ["./rtb-balance.component.scss"]
})
export class RtbBalanceComponent implements OnChanges {
	@Input()
	asset: APIResults.FleetAsset;
	@Input()
	fleet: APIResults.Fleet;
	@Input()
	rotor: APIResults.Rotor;
	@Input()
	regimeConfigs: APIResults.RegimeIndex;
	@Output()
	recordAdjustment = new EventEmitter<AdjustmentArchive>();

	public operations: APIResults.Operation[];
	public multiOps = false;
	public operation?: APIResults.Operation;
	public multiOperation?: APIResults.Operation[];
	public regimeCounts: APIResults.RegimeCount[];
	public selectedRegimes: number[];
	public adjustments?: number[][];
	public alternates?: number[][][];
	public shaftOrder = 1;
	public adjustmentSelect: boolean[];
	public vshafts?: VShaftWithPlot[];
	public errormessage?: string;
	public onePerRevOnly = true;
	public defaultVshaft = undefined;

	public adjDetail: boolean[];

	constructor(private api: ApiService, private charting: ChartingService) { }

	ngOnChanges() {
		this.vshafts = this.rotor.vshaft;
		const findDefault = this.vshafts.find(item => item.default);
		if (findDefault) {
			this.defaultVshaft = findDefault.name;
		} else {
			this.defaultVshaft = this.vshafts[0].name;
		}
		this.adjustmentSelect = Array(this.rotor.balancedata.numadjustmenttypes).fill(true);
		this.adjDetail = Array(this.rotor.balancedata.numadjustmenttypes).fill(false);
		this.refreshOperations();
	}

	async refreshOperations() {
		this.operations = undefined;
		this.operation = undefined;
		this.regimeCounts = undefined;
		const operations = await this.api.GetRTBOperations(this.asset.asset_id, this.rotor.rotorid, false, 800);
		this.operations = operations;
		if (this.operations.length > 0) {
			let selectedOp: APIResults.Operation;
			// Currently tries to select first op with rotor turn >= 5 minutes
			if (!BrandVars.fixedassets) {
				selectedOp = this.operations.find(item => item.rotor_turn_time > 300);
			}
			if (!selectedOp) {
				selectedOp = this.operations[0];
			}
			this.operation = selectedOp;
			this.multiOperation = [selectedOp];
			await this.refreshRegimeCounts();
		}
	}

	/** Must fire when an operation is selected to get the counts */
	async refreshRegimeCounts() {
		if (!this.multiOps) {
			if (this.operation && this.rotor) {
				this.regimeCounts = undefined;
				const counts = await this.api.GetRegimeCounts(this.asset.asset_id, this.rotor.rotorid, this.operation.operation_id);
				// Populate regime names
				if (counts && counts.vibe) {
					for (const regime of counts.vibe) {
						regime.name = this.regimeConfigs[regime.regime_id].name;
						regime.evaluate = this.regimeConfigs[regime.regime_id].default;
					}
					// If no regimes are defaulted on, turn on the last one
					if (!counts.vibe.find(regime => regime.evaluate)) counts.vibe[counts.vibe.length - 1].evaluate = true;
					this.regimeCounts = counts.vibe;
				}
			}
		} else {
			if (this.multiOperation && this.rotor) {
				// Can only select 5 operations
				if (this.multiOperation.length > 5) {
					this.multiOperation = this.multiOperation.slice(0, 5);
				}
				this.regimeCounts = undefined;
				const builder = [];
				for (const op of this.multiOperation) {
					const counts = await this.api.GetRegimeCounts(this.asset.asset_id, this.rotor.rotorid, op.operation_id);
					// Populate regime names, sum up counts
					if (counts && counts.vibe) {
						for (const regime of counts.vibe) {
							let match = builder.find(item => item.regime_id === regime.regime_id);
							if (!match) {
								match = { regime_id: regime.regime_id, count: 0 };
								match.name = this.regimeConfigs[regime.regime_id].name;
								match.evaluate = this.regimeConfigs[regime.regime_id].default;
								builder.push(match);
							}
							match.count += regime.count;
						}
					}
				}
				// If no regimes are defaulted on, turn on the last one
				if (!builder.find(regime => regime.evaluate)) builder[builder.length - 1].evaluate = true;
				this.regimeCounts = builder;
			}
		}
		this.regimesChange();
	}

	regimesChange() {
		if (this.regimeCounts) {
			this.selectedRegimes = this.regimeCounts.filter(item => item.evaluate).map(item => item.regime_id);
			this.updatePlot();
		}
	}

	async updatePlot() {
		this.adjustments = undefined;
		this.alternates = undefined;
		this.errormessage = undefined;
		if (this.selectedRegimes && this.selectedRegimes.length) {
			if (this.vshafts) {
				for (const vshaft of this.vshafts) {
					vshaft.plotOptions = undefined;
				}
			}
			try {
				let results;
				if (this.multiOps) {
					results = await this.api.GetRTBDataMultiple(this.asset.asset_id, this.rotor.rotorid, this.multiOperation.map(item => item.operation_id), this.selectedRegimes, this.shaftOrder, this.adjustmentSelect, false, this.onePerRevOnly);
				} else {
					results = await this.api.GetRTBData(this.asset.asset_id, this.rotor.rotorid, this.operation.operation_id, this.selectedRegimes, this.shaftOrder, this.adjustmentSelect, false, this.onePerRevOnly);
				}
				this.fillPlot(results);
				this.adjustments = results.adjustments.map(item => item.map(cell => Number(cell.toFixed(1))));
				this.alternates = results.alternates;
			} catch (err) {
				if (err && err.message === "Empty data") {
					this.errormessage = "Couldn't load RTB data\nThe selected regime(s) may not be valid for 2/rev analysis"
				} else {
					console.log(err);
					this.errormessage = "Couldn't load RTB data";
				}
			}
		}
	}

	SetAdjustment(adjIdx: number, altIdx: number) {
		const tmpAdj = this.adjustments[adjIdx];
		this.adjustments[adjIdx] = this.alternates[adjIdx][altIdx];
		this.alternates[adjIdx][altIdx] = tmpAdj;
		this.adjDetail[adjIdx] = false;
	}

	async ManualAdjustment() {
		this.alternates = undefined;

		this.errormessage = undefined;
		if (this.selectedRegimes && this.selectedRegimes.length) {
			if (this.vshafts) {
				for (const vshaft of this.vshafts) {
					vshaft.plotOptions = undefined;
				}
			}
			try {
				const results = await this.api.GetRTBManual(this.asset.asset_id, this.rotor.rotorid, this.operation.operation_id, this.selectedRegimes, this.shaftOrder, this.adjustmentSelect, this.adjustments, false, this.onePerRevOnly);
				this.fillPlot(results);
			} catch (err) {
				console.log(err);
				this.errormessage = "Couldn't load RTB data";
			}
		}
	}

	private fillPlot(rtbResults: APIResults.RTBResults) {
		if (this.vshafts) {
			for (const vshaft of this.vshafts) {
				const comp = rtbResults.data[vshaft.shftid];
				if (comp) {
					const series: Highcharts.SeriesScatterOptions[] = [];
					for (const regimeKey of Object.keys(comp.regimes)) {
						const regime = this.regimeCounts.find(item => item.regime_id === Number(regimeKey));
						const regimedata = comp.regimes[regimeKey];
						const points: any[] = [];
						if (regimedata.data.length === 0) throw new Error("Empty data");
						// Offset phase by vshaft.rotate if available
						points.push(...regimedata.data.map(point => [DvsUtilities.AddDegreeOffset(point[0], vshaft.rotate), point[1]]));
						points.push({
							x: DvsUtilities.AddDegreeOffset(regimedata.prediction[0], vshaft.rotate),
							y: regimedata.prediction[1],
							name: "Prediction",
							marker: { fillColor: "#DBCD00", radius: 6 },
						});
						points.push({
							x: DvsUtilities.AddDegreeOffset(regimedata.average[0], vshaft.rotate),
							y: regimedata.average[1],
							name: "Average",
							marker: { fillColor: "#027A85", radius: 5 },
						});
						series.push({
							name: regime.name,
							type: "scatter",
							data: points,
						});
					}
					vshaft.plotOptions = this.charting.RTBScatterDefaults(series);
				} else {
					vshaft.plotOptions = undefined;
				}
			}
		}
	}

	RecordAdjustment() {
		const archive: AdjustmentArchive = {
			operation_id: this.operation.operation_id,
			operation_start: this.operation.start_time,
			adjustments: this.adjustments,
		};
		// TODO: this is no longer valid
		this.recordAdjustment.emit(archive);
	}
	DownloadData(vshaft: VShaftWithPlot) {
		let output = "regime,avgmag,avgphase,predmag,predphase\n";
		for (const series of vshaft.plotOptions.series as SeriesLineOptions[]) {
			const avg: any = series.data.find((item: any) => item.name === "Average");
			const pred: any = series.data.find((item: any) => item.name === "Prediction");
			output += '"' + series.name + '",' + avg.y.toFixed(5) + "," + avg.x.toFixed(5) + "," + pred.y.toFixed(5) + "," + pred.x.toFixed(5) + "\n";
		}
		this.api.DownloadFile(vshaft.name + ".csv", output);
	}

	public FilterBlades(displaygroup: string) {
		return this.rotor.blade.filter(item => item.displaygroup === displaygroup);
	}
}

interface VShaftWithPlot extends APIResults.VShaft {
	plotOptions?: Highcharts.Options;
}
